import { sampleClients, sampleEvents, sampleNodes, samplePartners, samplePermissions, sampleUser } from "./sampleData";
import type {
  CallerSheetSource,
  ClientPerformanceData,
  ClientRecord,
  ClientStage,
  CrmEvent,
  CurrentUser,
  GoogleSheetsConfig,
  NodeRecord,
  PartnerRecord,
  Permissions,
  ProofPacket,
  ProofPacketDetail,
  ProofRecommendation,
  PulsePacket,
  WarmEntityType
} from "./types";

function cookieValue(name: string): string {
  if (typeof document === "undefined") return "";
  return (
    document.cookie
      .split(";")
      .map((part) => part.trim())
      .find((part) => part.startsWith(`${name}=`))
      ?.slice(name.length + 1) ?? ""
  );
}

function csrfHeaders(method?: string): HeadersInit {
  const normalizedMethod = (method || "GET").toUpperCase();
  if (["GET", "HEAD", "OPTIONS"].includes(normalizedMethod)) return {};
  const token = cookieValue("bl_crm_csrf");
  return token ? { "x-csrf-token": token } : {};
}

async function requestJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...csrfHeaders(init?.method),
      ...init?.headers
    }
  });
  if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
  return response.json() as Promise<T>;
}

export async function login(email: string, password: string) {
  return requestJson<{ ok: boolean; user: CurrentUser }>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password })
  });
}

export async function getMe() {
  try {
    return await requestJson<{ user: CurrentUser; permissions: Permissions }>("/api/auth/me");
  } catch (error) {
    if (import.meta.env.DEV) return { user: sampleUser, permissions: samplePermissions };
    throw error;
  }
}

export async function getClients(): Promise<ClientRecord[]> {
  try {
    const data = await requestJson<{ clients: ClientRecord[] }>("/api/clients?limit=100");
    return data.clients;
  } catch (error) {
    if (import.meta.env.DEV) return sampleClients;
    throw error;
  }
}

export async function getClient(id: string): Promise<ClientRecord> {
  const data = await requestJson<{ client: ClientRecord }>(`/api/clients/${id}`);
  return data.client;
}

export async function createClient(input: Record<string, unknown>): Promise<ClientRecord> {
  const data = await requestJson<{ client: ClientRecord }>("/api/clients", {
    method: "POST",
    body: JSON.stringify(input)
  });
  return data.client;
}

export async function updateClient(id: string, input: Record<string, unknown>): Promise<ClientRecord> {
  const data = await requestJson<{ client: ClientRecord }>(`/api/clients/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input)
  });
  return data.client;
}

export async function transitionClientStage(id: string, stage: ClientStage) {
  return requestJson<{ ok: boolean; stage: ClientStage; outboxEnqueued: boolean }>(`/api/clients/${id}/stage`, {
    method: "POST",
    body: JSON.stringify({ stage })
  });
}

export async function getPartners(): Promise<PartnerRecord[]> {
  try {
    const data = await requestJson<{ partners: PartnerRecord[] }>("/api/partners?limit=100");
    return data.partners;
  } catch (error) {
    if (import.meta.env.DEV) return samplePartners;
    throw error;
  }
}

export async function createPartner(input: Record<string, unknown>): Promise<PartnerRecord> {
  const data = await requestJson<{ partner: PartnerRecord }>("/api/partners", {
    method: "POST",
    body: JSON.stringify(input)
  });
  return data.partner;
}

export async function updatePartner(id: string, input: Record<string, unknown>): Promise<PartnerRecord> {
  const data = await requestJson<{ partner: PartnerRecord }>(`/api/partners/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input)
  });
  return data.partner;
}

export async function getNodes(): Promise<NodeRecord[]> {
  try {
    const data = await requestJson<{ nodes: NodeRecord[] }>("/api/nodes?limit=100");
    return data.nodes;
  } catch (error) {
    if (import.meta.env.DEV) return sampleNodes;
    throw error;
  }
}

export async function createNode(input: Record<string, unknown>): Promise<NodeRecord> {
  const data = await requestJson<{ node: NodeRecord }>("/api/nodes", {
    method: "POST",
    body: JSON.stringify(input)
  });
  return data.node;
}

export async function updateNode(id: string, input: Record<string, unknown>): Promise<NodeRecord> {
  const data = await requestJson<{ node: NodeRecord }>(`/api/nodes/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input)
  });
  return data.node;
}

export async function getEvents(): Promise<CrmEvent[]> {
  try {
    const data = await requestJson<{ events: CrmEvent[] }>("/api/events?limit=100");
    return data.events;
  } catch (error) {
    if (import.meta.env.DEV) return sampleEvents;
    throw error;
  }
}

export async function updateReportSchedule(clientId: string, input: Record<string, unknown>) {
  return requestJson<{ ok: boolean }>(`/api/reports/schedules/${clientId}`, {
    method: "PATCH",
    body: JSON.stringify(input)
  });
}

export async function processReports() {
  return requestJson<{ result: unknown }>("/api/reports/process", { method: "POST" });
}

export interface AwaitingClientReportDelivery {
  id: string;
  client_id: string;
  client_name: string;
  schedule_due_at: string;
  recipient_email: string;
  subject: string;
  packet_queued_at: string | null;
}

export async function getAwaitingClientReportDeliveries() {
  return requestJson<{ deliveries: AwaitingClientReportDelivery[] }>("/api/reports/deliveries");
}

export async function confirmClientReportSent(id: string) {
  return requestJson<{ ok: boolean }>(`/api/reports/deliveries/${id}/confirm`, { method: "POST" });
}

export async function getProofPackets(input: { clientId?: string; leadClientId?: string } = {}) {
  const params = new URLSearchParams();
  if (input.clientId) params.set("clientId", input.clientId);
  if (input.leadClientId) params.set("leadClientId", input.leadClientId);
  const query = params.toString();
  return requestJson<{ proofPackets: ProofPacket[]; recommendations?: ProofRecommendation[] }>(`/api/proof${query ? `?${query}` : ""}`);
}

export async function generateProofPacket(clientId: string) {
  return requestJson<{ ok: boolean; proofPacket: ProofPacket }>("/api/proof/generate", {
    method: "POST",
    body: JSON.stringify({ clientId })
  });
}

export async function updateProofPacketStatus(id: string, status: ProofPacket["status"]) {
  return requestJson<{ ok: boolean; proofPacket: ProofPacket }>(`/api/proof/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ status })
  });
}

export async function getProofPacketDetail(id: string) {
  return requestJson<ProofPacketDetail>(`/api/proof/${id}`);
}

export async function attachProofRecommendation(recommendationId: string) {
  return requestJson<{ ok: boolean }>("/api/proof/recommendations/attach", {
    method: "POST",
    body: JSON.stringify({ recommendationId })
  });
}

export async function getPulsePackets(input: { clientId?: string } = {}) {
  const params = new URLSearchParams();
  if (input.clientId) params.set("clientId", input.clientId);
  const query = params.toString();
  return requestJson<{ pulsePackets: PulsePacket[] }>(`/api/pulse${query ? `?${query}` : ""}`);
}

export async function generatePulsePacket(clientId: string) {
  return requestJson<{ ok: boolean; pulsePacket: PulsePacket }>("/api/pulse/generate", {
    method: "POST",
    body: JSON.stringify({ clientId })
  });
}

export async function updatePulsePacketStatus(id: string, status: PulsePacket["status"]) {
  return requestJson<{ ok: boolean; pulsePacket: PulsePacket }>(`/api/pulse/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ status })
  });
}

export async function getClientPerformance(clientId: string) {
  return requestJson<ClientPerformanceData>(`/api/performance?clientId=${encodeURIComponent(clientId)}`);
}

export async function saveClientConnections(clientId: string, input: Record<string, unknown>) {
  return requestJson<{ ok: boolean }>("/api/performance/connections", {
    method: "POST",
    body: JSON.stringify({ clientId, ...input })
  });
}

export async function provisionClientGa4(clientId: string) {
  return requestJson<{ ok: boolean; propertyId: string; measurementId: string; websiteUrl: string }>("/api/performance/provision-ga4", {
    method: "POST",
    body: JSON.stringify({ clientId })
  });
}

export async function syncClientPerformance(clientId: string) {
  return requestJson<{ ok: boolean; statuses: Record<string, string>; period: { start: string; end: string } }>("/api/performance/sync", {
    method: "POST",
    body: JSON.stringify({ clientId })
  });
}

export async function recordAutomationMetrics(clientId: string, input: Record<string, unknown>) {
  return requestJson<{ ok: boolean }>("/api/performance/automation", {
    method: "POST",
    body: JSON.stringify({ clientId, ...input })
  });
}

export async function processTelegramOutbox() {
  return requestJson<{ result: unknown }>("/api/outbox/telegram/process", { method: "POST" });
}

export async function importCsv(kind: "master" | "b-online", file: File): Promise<{ rowsSeen?: number; rowsImported?: number; rowsUpdated?: number }> {
  const text = await file.text();
  const endpoint = kind === "master" ? "/api/import/master" : "/api/import/b-online";
  return fetch(`${endpoint}?source=${encodeURIComponent(file.name)}`, {
    method: "POST",
    body: text,
    headers: { "content-type": "text/csv; charset=utf-8", ...csrfHeaders("POST") }
  }).then((response) => {
    if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
    return response.json() as Promise<{ rowsSeen?: number; rowsImported?: number; rowsUpdated?: number }>;
  });
}

export async function syncGoogleSheets() {
  return requestJson<{ results: unknown[] }>("/api/google-sheets/sync", { method: "POST" });
}

export async function getCallerSheetSources(): Promise<CallerSheetSource[]> {
  try {
    const data = await requestJson<{ sources: CallerSheetSource[] }>("/api/google-sheets/sources");
    return data.sources;
  } catch (error) {
    if (import.meta.env.DEV) {
      return [
        {
          id: "demo-sheet",
          name: "Weekly Caller Sheet",
          spreadsheet_id: "demo-spreadsheet-id",
          sheet_name: "Warm Leads",
          range_name: "A:Z",
          enabled: 1,
          last_synced_at: "2026-06-20T09:30:00Z"
        }
      ];
    }
    throw error;
  }
}

export async function getGoogleSheetsConfig(): Promise<GoogleSheetsConfig> {
  return requestJson<GoogleSheetsConfig>("/api/google-sheets/config");
}

export async function registerCallerSheet(input: { name: string; spreadsheetId: string; sheetName?: string; rangeName?: string }) {
  return requestJson<{ id: string; ok: boolean }>("/api/google-sheets/sources", {
    method: "POST",
    body: JSON.stringify(input)
  });
}

export function entityLabel(type: WarmEntityType) {
  if (type === "client") return "Client";
  if (type === "partner") return "Partner";
  return "Node";
}
